// Session PJ2: deleting a project survey. The office is first told what the server says a delete would do (GET delete-check): any
// reason it is refused, and what goes with it (photos, the visit booked only for it, a supplier draft already made). A SUBMITTED
// survey is admin only and needs its name typed; the shared two-step ConfirmDeleteModal then asks twice. Photos already in Drive are
// left there and the modal says so.
import { useEffect, useState } from "react";
import { api, type ProjectSurveyDeleteCheck } from "../../../lib/api";
import { friendlyApiError } from "../../../lib/friendlyApiError";
import { ConfirmDeleteModal } from "../../../components/ConfirmDeleteModal";
import { MODAL_ACTION_BTN, ModalShell } from "../../../components/ModalShell";

const inputCls = "mt-3 w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green max-sm:min-h-11";

export function SurveyDeleteModal({
  survey,
  onClose,
  onDeleted,
}: {
  survey: { id: string; label: string | null; kind: "site" | "lift" };
  onClose: () => void;
  /** `notice` is the server's words when a visit booked for the survey could not be cancelled and stays booked. */
  onDeleted: (notice: string | null) => void;
}) {
  const [check, setCheck] = useState<ProjectSurveyDeleteCheck | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A refused name starts the two presses again: the modal is made afresh, so the next press arms it rather than deleting.
  const [round, setRound] = useState(0);
  const name = survey.kind === "site" ? "Site survey" : (survey.label ?? "this lift");

  useEffect(() => {
    let live = true;
    api.projectSurveysOffice
      .deleteCheck(survey.id)
      .then((c) => {
        if (live) setCheck(c);
      })
      .catch((e) => {
        if (live) setLoadError(friendlyApiError(e, "Could not check what deleting this survey would do."));
      });
    return () => {
      live = false;
    };
  }, [survey.id]);

  if (loadError || !check || !check.allowed) {
    return (
      <ModalShell ariaLabel="Delete survey" onClose={onClose} closeOnBackdrop width="max-w-md" panelClassName="p-5">
        <h2 className="font-heading text-lg text-ele-strong">Delete {name}?</h2>
        {loadError ? (
          <p role="alert" className="mt-2 text-sm text-ele-danger">{loadError}</p>
        ) : !check ? (
          <p className="mt-2 text-sm text-ele-body" role="status">Checking what would be deleted...</p>
        ) : (
          <>
            <p className="mt-2 text-sm text-ele-body">This survey cannot be deleted:</p>
            <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-ele-strong">
              {check.refusals.map((r) => <li key={r}>{r}</li>)}
            </ul>
          </>
        )}
        <div className="mt-5 flex justify-end">
          <button type="button" onClick={onClose} className={`rounded-ele border border-ele-purple-accent px-4 py-2 text-sm ${MODAL_ACTION_BTN}`}>
            Close
          </button>
        </div>
      </ModalShell>
    );
  }

  const g = check.goesWith;
  const parts: string[] = [];
  parts.push(`${g.photos} photo${g.photos === 1 ? "" : "s"} recorded against it`);
  if (g.visitsCancelled > 0) parts.push(`the booked visit for it (${g.visitsCancelled} visit${g.visitsCancelled === 1 ? "" : "s"} cancelled, and the engineer's calendar updated)`);
  const kept = g.visitsKept > 0 ? ` ${g.visitsKept} visit${g.visitsKept === 1 ? "" : "s"} also cover other surveys, so ${g.visitsKept === 1 ? "it stays" : "they stay"}.` : "";
  const drafted = g.supplierDraft ? " A supplier data sheet was already drafted from it; that Gmail draft is not removed." : "";
  const left = g.driveFilesLeft > 0 ? ` The ${g.driveFilesLeft} photo file${g.driveFilesLeft === 1 ? "" : "s"} already filed in Google Drive stay${g.driveFilesLeft === 1 ? "s" : ""} there.` : "";

  async function confirm() {
    if (check!.confirmLabelRequired && typed.trim().toLowerCase() !== name.trim().toLowerCase()) {
      setError(`Type the survey's name (${name}) exactly to delete a submitted survey.`);
      setRound((r) => r + 1);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const out = await api.projectSurveysOffice.remove(survey.id, check!.confirmLabelRequired ? typed.trim() : undefined);
      onDeleted(out?.message ?? null);
    } catch (e) {
      setError(friendlyApiError(e, "Could not delete the survey."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ConfirmDeleteModal
      key={round}
      open
      title={`Delete ${name}?`}
      message={`This permanently deletes ${name} and takes with it ${parts.join(" and ")}.${kept}${drafted}${left}`}
      warning={check.confirmLabelRequired ? "This survey has been submitted. Deleting it removes what the engineer recorded." : null}
      confirmLabel="Delete survey"
      busy={busy}
      error={error}
      onConfirm={() => void confirm()}
      onCancel={() => {
        if (!busy) onClose();
      }}
    >
      {check.confirmLabelRequired ? (
        <>
          <label htmlFor="survey-delete-name" className="mt-3 block text-sm text-ele-body">
            Type <strong className="text-ele-strong">{name}</strong> to confirm
          </label>
          <input id="survey-delete-name" className={inputCls} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
        </>
      ) : null}
    </ConfirmDeleteModal>
  );
}
